import type { AdminAppointment } from './types';

export interface AppointmentCardModel {
  id: string;
  title: string;
  timeRange: string;
  statusLabel: string;
  providerLabel: string;
  issueLabels: string[];
  attendeeLabels: string[];
  historyHref: string;
}

const issueLabels: Record<AdminAppointment['issues'][number]['code'], string> = {
  calendar_conflict: 'Calendar conflict',
  participant_mismatch: 'Participant mismatch',
  operator_review_required: 'Operator review required',
};

const bookingLabels: Record<AdminAppointment['booking_status'], string> = {
  confirmed: 'Confirmed',
  pending: 'Pending provider confirmation',
  rejected: 'Rejected',
};

const lifecycleLabels: Record<AdminAppointment['lifecycle_status'], string> = {
  scheduled: 'Scheduled',
  cancellation_pending: 'Cancellation pending',
  cancelled: 'Cancelled',
  reschedule_pending: 'Reschedule pending',
};

export function toAppointmentCardModel(appointment: AdminAppointment): AppointmentCardModel {
  return {
    id: appointment.id,
    title: `${capitalize(appointment.kind)} · ${appointment.expected_identities.member.display_name}`,
    timeRange: formatTimeRange(
      appointment.current_schedule.start_at,
      appointment.current_schedule.end_at,
      appointment.current_schedule.timezone
    ),
    statusLabel: `${bookingLabels[appointment.booking_status]} · ${lifecycleLabels[appointment.lifecycle_status]}`,
    providerLabel: providerLabel(appointment),
    issueLabels: appointment.issues.map((issue) => issueLabels[issue.code]),
    attendeeLabels: appointment.observed_attendees.map(attendeeLabel),
    historyHref: `/admin/appointments/${encodeURIComponent(appointment.id)}`,
  };
}

function providerLabel(appointment: AdminAppointment): string {
  const binding = appointment.active_provider_binding;
  if (!binding) return 'No active provider binding';
  const observed = binding.observed_at
    ? ` observed ${formatInstant(binding.observed_at, 'UTC')}`
    : '';
  return `${capitalize(binding.provider)} event ${binding.event_id}${observed}`;
}

function attendeeLabel(attendee: AdminAppointment['observed_attendees'][number]): string {
  return [
    `${attendee.display_name ?? 'Unknown attendee'} <${attendee.email}>`,
    attendee.expected_role ?? 'unexpected',
    attendee.response_status,
    attendee.organizer ? 'organizer' : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
}

function formatTimeRange(start: string, end: string, timezone: string): string {
  return `${formatInstant(start, timezone, false)} - ${formatTime(end, timezone)}`;
}

function formatInstant(instant: string, timezone: string, includeTimeZone = true): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: includeTimeZone ? 'short' : undefined,
  }).format(new Date(instant));
}

function formatTime(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: 'short',
  }).format(new Date(instant));
}

function capitalize(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}
